import { apiSlice } from './apiSlice'

export type EventType =
  | 'session_open'
  | 'team_intro'
  | 'evidence_disclosed'
  | 'argument'
  | 'judge_question'
  | 'team_response'
  | 'objection'
  | 'recess'
  | 'closing_statement'
  | 'verdict_delivered'
  | 'session_close'
  | 'other'

export interface HearingMinuteEntry {
  timestamp: string
  event_type: EventType
  actor?: string | null
  actor_label?: string | null
  content: string
  related_evidence_id?: string | null
  notes?: string | null
}

export interface HearingMinute {
  id?: string
  courtroom_id: string
  case_id: string
  round_id: string
  secretary_id?: string | null
  started_at?: string | null
  ended_at?: string | null
  status: 'draft' | 'finalized'
  entries: HearingMinuteEntry[]
  summary?: string | null
  finalized_at?: string | null
  finalized_by?: string | null
  created_at?: string | null
  updated_at?: string | null
}

export interface OpenMinutePayload {
  courtroom_id: string
  case_id: string
  round_id: string
}

export interface AddEntryPayload {
  event_type: EventType
  content: string
  actor?: string
  actor_label?: string
  related_evidence_id?: string
  notes?: string
  timestamp?: string
}

export const hearingMinutesApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getMinuteForCourtroom: builder.query<HearingMinute | null, string>({
      query: (courtroomId) => `/hearing-minutes/courtroom/${courtroomId}`,
      providesTags: (res, err, id) => [{ type: 'HearingMinute', id }],
    }),
    openMinute: builder.mutation<HearingMinute, OpenMinutePayload>({
      query: (body) => ({ url: '/hearing-minutes/open', method: 'POST', body }),
      invalidatesTags: (res, err, body) => [
        { type: 'HearingMinute', id: body.courtroom_id },
      ],
    }),
    addMinuteEntry: builder.mutation<
      HearingMinute,
      { minute_id: string; entry: AddEntryPayload; courtroom_id: string }
    >({
      query: ({ minute_id, entry }) => ({
        url: `/hearing-minutes/${minute_id}/entries`,
        method: 'POST',
        body: entry,
      }),
      // Optimistic patch
      async onQueryStarted({ minute_id, entry, courtroom_id }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          hearingMinutesApi.util.updateQueryData(
            'getMinuteForCourtroom',
            courtroom_id,
            (draft) => {
              if (draft) {
                draft.entries.push({
                  ...entry,
                  timestamp: entry.timestamp || new Date().toISOString(),
                } as HearingMinuteEntry)
              }
            },
          ),
        )
        try {
          const { data: real } = await queryFulfilled
          dispatch(
            hearingMinutesApi.util.updateQueryData(
              'getMinuteForCourtroom',
              courtroom_id,
              () => real,
            ),
          )
        } catch {
          patch.undo?.()
        }
      },
    }),
    deleteMinuteEntry: builder.mutation<
      HearingMinute,
      { minute_id: string; index: number; courtroom_id: string }
    >({
      query: ({ minute_id, index }) => ({
        url: `/hearing-minutes/${minute_id}/entries/${index}`,
        method: 'DELETE',
      }),
      invalidatesTags: (res, err, { courtroom_id }) => [
        { type: 'HearingMinute', id: courtroom_id },
      ],
    }),
    finalizeMinute: builder.mutation<
      HearingMinute,
      { minute_id: string; summary?: string; courtroom_id: string }
    >({
      query: ({ minute_id, summary }) => ({
        url: `/hearing-minutes/${minute_id}/finalize`,
        method: 'POST',
        body: { summary },
      }),
      invalidatesTags: (res, err, { courtroom_id }) => [
        { type: 'HearingMinute', id: courtroom_id },
      ],
    }),
    listMinutesForCase: builder.query<HearingMinute[], string>({
      query: (caseId) => `/hearing-minutes/case/${caseId}`,
    }),
  }),
})

export const {
  useGetMinuteForCourtroomQuery,
  useOpenMinuteMutation,
  useAddMinuteEntryMutation,
  useDeleteMinuteEntryMutation,
  useFinalizeMinuteMutation,
  useListMinutesForCaseQuery,
} = hearingMinutesApi
